/**
 * ConferenceDemoPage — a self-contained, deterministic marketing demo used to
 * capture the "Cloverleaf Autonomous Analytics" 45-second conference video.
 * Fictional carrier: Summit Mutual Insurance (Commercial Auto / Southeast).
 * All values are hardcoded so every frame tells the same story and nothing
 * recalculates between captures. Public route — no app chrome, no login.
 *
 * States (?state=): brand-open | overview | detected-risk | forecast |
 * root-cause | recommendation | recommendation-evidence | governance | brand-close
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ComposedChart, Area, Line, XAxis, YAxis, ResponsiveContainer, ReferenceArea,
} from 'recharts';
import {
  Activity, AlertTriangle, ArrowUpRight, ArrowDownRight, TrendingUp, ShieldCheck,
  Sparkles, CheckCircle2, ChevronRight, Clock, Target, BadgeCheck, GitBranch,
  Search, FileCheck2, Send, ClipboardList, Database, Gauge,
  Brain, Building2, Lock,
} from 'lucide-react';

/* ── Brand tokens (Cloverleaf) — theme-switchable via ?theme=light ────── */
let BG = '#060918';
let SURFACE = '#0B1021';
let SURFACE2 = '#121A33';
let BORDER = 'rgba(255,255,255,0.08)';
const GREEN = '#36B44B';
const GREEN_LT = '#68C777';
const GREEN_SOFT = 'rgba(54,180,75,0.15)';
const AMBER = '#F5A623';
const RED = '#FF4D4F';
const RED_SOFT = 'rgba(255,77,79,0.14)';
let TEXT = '#F8FAFC';
let MUTED = '#93A1B8';
let CARD_SHADOW = '0 8px 32px rgba(0,0,0,0.4)';
let PANEL_BG = 'rgba(9,13,28,0.90)';
let PANEL_SHADOW = '0 24px 70px rgba(0,0,0,0.6)';
let VIGNETTE = 'radial-gradient(132% 104% at 50% 44%, rgba(6,9,24,0) 58%, rgba(6,9,24,0.46) 100%)';
let GLOW1 = 'rgba(54,180,75,0.18)';
let GLOW2 = 'rgba(59,130,246,0.12)';
let TINT = 'rgba(11,16,33,0.6)';
let WORDMARK = '/cloverleaf/wordmark_dark.png';
let LEADER_GREEN = '#36B44B';
const HEAD = "'Outfit','Plus Jakarta Sans',sans-serif";
const BODY = "'Plus Jakarta Sans','Outfit',sans-serif";

function applyTheme(light) {
  if (light) {
    BG = '#EEF2F7'; SURFACE = '#FFFFFF'; SURFACE2 = '#F1F5F9'; BORDER = 'rgba(15,23,42,0.12)';
    TEXT = '#0B1220'; MUTED = '#556072';
    CARD_SHADOW = '0 8px 30px rgba(15,23,42,0.10)';
    PANEL_BG = 'rgba(255,255,255,0.94)'; PANEL_SHADOW = '0 22px 60px rgba(15,23,42,0.18)';
    VIGNETTE = 'radial-gradient(132% 104% at 50% 44%, rgba(15,23,42,0) 62%, rgba(15,23,42,0.13) 100%)';
    GLOW1 = 'rgba(54,180,75,0.13)'; GLOW2 = 'rgba(59,130,246,0.10)';
    TINT = 'rgba(255,255,255,0.78)'; WORDMARK = '/cloverleaf/wordmark.png';
    LEADER_GREEN = '#1E9E37';
  } else {
    BG = '#060918'; SURFACE = '#0B1021'; SURFACE2 = '#121A33'; BORDER = 'rgba(255,255,255,0.08)';
    TEXT = '#F8FAFC'; MUTED = '#93A1B8';
    CARD_SHADOW = '0 8px 32px rgba(0,0,0,0.4)';
    PANEL_BG = 'rgba(9,13,28,0.90)'; PANEL_SHADOW = '0 24px 70px rgba(0,0,0,0.6)';
    VIGNETTE = 'radial-gradient(132% 104% at 50% 44%, rgba(6,9,24,0) 58%, rgba(6,9,24,0.46) 100%)';
    GLOW1 = 'rgba(54,180,75,0.18)'; GLOW2 = 'rgba(59,130,246,0.12)';
    TINT = 'rgba(11,16,33,0.6)'; WORDMARK = '/cloverleaf/wordmark_dark.png';
    LEADER_GREEN = '#36B44B';
  }
}

/* ── Motion (settles to a clean final frame; disable with ?motion=0) ─── */
let MOTION = true;
// True only during autoplay film mode; hero value pulses use it to fire in sync
// with the leader line landing on the evidence (LEADER_LAND seconds into a scene).
let FILM_ON = false;
const LEADER_LAND = 1.95;
const KEYFRAMES = `
@keyframes clFade{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}}
@keyframes clScale{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:none}}
@keyframes clGrowX{from{transform:scaleX(0)}to{transform:scaleX(1)}}
@keyframes clPop{from{opacity:0;transform:scale(.82)}to{opacity:1;transform:none}}
@keyframes clPulse{0%,100%{box-shadow:0 0 0 0 rgba(255,77,79,0)}50%{box-shadow:0 0 0 8px rgba(255,77,79,.13)}}
@keyframes clGlow{0%,100%{box-shadow:0 0 0 0 rgba(54,180,75,0)}50%{box-shadow:0 0 24px 2px rgba(54,180,75,.4)}}
@keyframes clSlideL{from{opacity:0;transform:translateX(-52px)}to{opacity:1;transform:none}}
@keyframes clSlideOut{from{opacity:1;transform:none}to{opacity:0;transform:translateX(-44px)}}
@keyframes clValuePulse{0%{transform:scale(1)}42%{transform:scale(1.34)}100%{transform:scale(1)}}
@keyframes cdDraw{to{stroke-dashoffset:0}}
@keyframes cdRing{0%,100%{opacity:.9;transform:scale(1)}50%{opacity:.35;transform:scale(1.5)}}
@keyframes cdSceneIn{from{opacity:0}to{opacity:1}}
`;
function anim(name, delay = 0, dur = 0.55) {
  return MOTION ? { animation: `${name} ${dur}s cubic-bezier(.22,.61,.36,1) ${delay}s both` } : {};
}

/* ── Cloverleaf logo (official assets) ───────────────────────────────── */
function Clover({ size = 44 }) {
  return <img src="/cloverleaf/mark.png" alt="Cloverleaf" width={size} height={size} style={{ display: 'block', objectFit: 'contain' }} />;
}

function Wordmark({ scale = 1 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 * scale }}>
      <Clover size={40 * scale} />
      <img src={WORDMARK} alt="Cloverleaf Analytics" style={{ height: 30 * scale, display: 'block' }} />
    </div>
  );
}

/* ── Shared chrome ───────────────────────────────────────────────────── */
function Frame({ children, pad = true, testid }) {
  return (
    <div
      data-testid={testid || 'demo-frame'}
      style={{
        width: '100vw', minHeight: '100vh', background: BG, color: TEXT, fontFamily: BODY,
        position: 'relative', overflow: 'hidden',
      }}
    >
      <style>{KEYFRAMES}</style>
      {/* atmospheric glows */}
      <div style={{ position: 'absolute', top: -260, left: -140, width: 680, height: 680, background: `radial-gradient(circle, ${GLOW1}, transparent 62%)`, filter: 'blur(30px)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -300, right: -160, width: 720, height: 720, background: `radial-gradient(circle, ${GLOW2}, transparent 62%)`, filter: 'blur(30px)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(${BORDER} 1px, transparent 1px), linear-gradient(90deg, ${BORDER} 1px, transparent 1px)`, backgroundSize: '48px 48px', opacity: 0.35, pointerEvents: 'none' }} />
      <div style={{ position: 'relative', zIndex: 1, ...(pad ? { minHeight: '100vh', padding: '4vh 56px 17vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' } : { padding: 0 }) }}>{children}</div>
    </div>
  );
}

function AppHeader({ scanTime = '6:04 AM' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 26 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <Wordmark />
        <div style={{ width: 1, height: 30, background: BORDER }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 14px', borderRadius: 999, background: SURFACE, border: `1px solid ${BORDER}` }}>
          <div style={{ width: 8, height: 8, borderRadius: 999, background: GREEN }} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>Summit Mutual Insurance</span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: MUTED, fontSize: 14 }}>
        <Activity size={16} color={GREEN} />
        <span>Autonomous scan completed {scanTime}</span>
      </div>
    </div>
  );
}

function Card({ children, style, delay = 0, kf = 'clFade', pulse }) {
  const base = MOTION ? `${kf} .55s cubic-bezier(.22,.61,.36,1) ${delay}s both` : '';
  const extra = MOTION && pulse ? `, clPulse 1.3s ease ${delay + 0.2}s 1 both` : '';
  return (
    <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 20, boxShadow: CARD_SHADOW, ...style, animation: (base + extra) || undefined }}>
      {children}
    </div>
  );
}

function Kicker({ children }) {
  return <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: GREEN }}>{children}</div>;
}

/* ── Trend / forecast series (deterministic) ─────────────────────────── */
const MONTHS = ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];
function buildSeries(withForecast) {
  // 24 historical months; actual tracks expected then diverges in last 5.
  const base = 20800;
  const recent = { 19: 23600, 20: 24400, 21: 25500, 22: 26600, 23: 27860 };
  const data = MONTHS.map((m, i) => {
    const exp = base + i * 120 + (i % 3 === 0 ? 90 : 0);
    const lo = Math.round(exp * 0.955);
    const hi = Math.round(exp * 1.045);
    let act;
    if (i < 19) act = Math.round(exp + Math.sin(i * 1.3) * 260 + (i % 2 ? 120 : -80));
    else act = recent[i];
    return { m, i, exp, lo, hi, band: [lo, hi], act, key: `${m}${i}` };
  });
  if (!withForecast) return data;
  const last = data[data.length - 1];
  const fMonths = ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];
  const fc = fMonths.map((m, j) => {
    const i = data.length + j;
    const val = Math.round(last.act + (j + 1) * 430);
    const spread = 900 + j * 190;
    return { m, i, fc: val, flo: val - spread, fhi: val + spread, fband: [val - spread, val + spread], key: `f${m}${j}` };
  });
  // bridge point so lines connect
  const bridge = { ...last, fc: last.act, fband: [last.act, last.act] };
  return [...data.slice(0, -1), bridge, ...fc];
}

/* ── Metric card ─────────────────────────────────────────────────────── */
function Metric({ label, value, delta, tone, delay = 0 }) {
  const color = tone === 'bad' ? AMBER : GREEN;
  const Icon = tone === 'bad' ? ArrowUpRight : (String(delta).trim().startsWith('−') || String(delta).trim().startsWith('-') ? ArrowDownRight : ArrowUpRight);
  return (
    <Card delay={delay} style={{ padding: '20px 22px', flex: 1 }}>
      <div style={{ fontSize: 14, color: MUTED, fontWeight: 600, marginBottom: 12 }}>{label}</div>
      <div style={{ fontFamily: HEAD, fontSize: 34, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8 }}>{value}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color }}>
        <Icon size={16} />
        <span style={{ fontSize: 15, fontWeight: 700 }}>{delta}</span>
      </div>
    </Card>
  );
}

/* ── Driver bar ──────────────────────────────────────────────────────── */
function Driver({ rank, label, pct, top, delay = 0, anchor }) {
  return (
    <div style={{ marginBottom: 18, ...anim('clFade', delay) }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 26, height: 26, borderRadius: 8, background: top ? GREEN_SOFT : SURFACE2, color: top ? GREEN : MUTED, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>{rank}</span>
          <span style={{ fontSize: 18, fontWeight: 600 }}>{label}</span>
        </div>
        <span style={{ fontFamily: HEAD, fontSize: 20, fontWeight: 800, color: top ? GREEN : TEXT }} {...(anchor ? { 'data-cd-anchor': '1' } : {})}>{pct}%</span>
      </div>
      <div style={{ height: 12, borderRadius: 999, background: SURFACE2, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, transformOrigin: 'left', background: top ? `linear-gradient(90deg, ${GREEN}, ${GREEN_LT})` : 'rgba(147,161,184,0.5)', ...anim('clGrowX', delay + 0.1, 0.8) }} />
      </div>
    </div>
  );
}

function Stat({ label, value, tone, delay = 0, pulse, anchor }) {
  const c = tone === 'bad' ? RED : tone === 'good' ? GREEN : TEXT;
  const pd = (anchor && FILM_ON) ? LEADER_LAND : delay + 0.15;
  const valAnim = MOTION && pulse ? { display: 'inline-block', transformOrigin: 'left', animation: `clValuePulse 0.85s ease ${pd}s 1 both` } : {};
  return (
    <div style={{ padding: '14px 6px', borderRadius: 10, borderBottom: `1px solid ${BORDER}`, ...anim('clFade', delay) }}>
      <div style={{ fontSize: 13, color: MUTED, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
      <div style={{ fontFamily: HEAD, fontSize: 22, fontWeight: 800, color: c }} {...(anchor ? { 'data-cd-anchor': '1' } : {})}><span style={valAnim}>{value}</span></div>
    </div>
  );
}

/* ── The chart ───────────────────────────────────────────────────────── */
function SeverityChart({ forecast }) {
  const data = buildSeries(forecast);
  const divergeStart = 19;
  return (
    <div style={{ width: '100%', height: 360 }}>
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 12, right: 20, left: 8, bottom: 4 }}>
          <defs>
            <linearGradient id="fcband" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={GREEN} stopOpacity={0.22} />
              <stop offset="100%" stopColor={GREEN} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis dataKey="m" tick={{ fill: MUTED, fontSize: 12 }} interval={2} axisLine={{ stroke: BORDER }} tickLine={false} />
          <YAxis tickFormatter={(v) => `$${Math.round(v / 1000)}k`} tick={{ fill: MUTED, fontSize: 12 }} axisLine={false} tickLine={false} domain={[18000, forecast ? 34000 : 30000]} width={54} />
          {/* expected range band */}
          <Area dataKey="band" stroke="none" fill="rgba(147,161,184,0.16)" isAnimationActive={MOTION} animationDuration={600} connectNulls />
          {/* forecast confidence band */}
          {forecast && <Area dataKey="fband" stroke="none" fill="url(#fcband)" isAnimationActive={MOTION} animationBegin={500} animationDuration={700} connectNulls />}
          {/* expected line */}
          <Line dataKey="exp" stroke={MUTED} strokeWidth={2} strokeDasharray="5 5" dot={false} isAnimationActive={MOTION} animationDuration={700} />
          {/* actual line */}
          <Line dataKey="act" stroke={RED} strokeWidth={3.5} dot={false} isAnimationActive={MOTION} animationBegin={200} animationDuration={900} connectNulls />
          {/* forecast line */}
          {forecast && <Line dataKey="fc" stroke={GREEN} strokeWidth={3.5} strokeDasharray="7 5" dot={false} isAnimationActive={MOTION} animationBegin={900} animationDuration={800} connectNulls />}
          {/* emphasise recent divergence */}
          <ReferenceArea x1={MONTHS[divergeStart]} x2={MONTHS[23]} fill={RED} fillOpacity={0.06} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function LegendDot({ color, dash, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 22, height: 0, borderTop: `3px ${dash ? 'dashed' : 'solid'} ${color}` }} />
      <span style={{ fontSize: 13, color: MUTED }}>{label}</span>
    </div>
  );
}

/* ═══════════════════════ SCENES ═══════════════════════ */

function BrandCard({ headline, lines, sub, sub2, site, greenLast = true }) {
  return (
    <Frame pad={false}>
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 8%' }}>
        <div style={{ marginBottom: 46, ...anim('clScale', 0, 0.6) }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 26 }}>
            <img src="/cloverleaf/mark.png" alt="Cloverleaf" style={{ width: 120, height: 120, objectFit: 'contain' }} />
            <img src={WORDMARK} alt="Cloverleaf Analytics" style={{ height: 62 }} />
          </div>
        </div>
        {headline && (
          <h1 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 60, lineHeight: 1.08, letterSpacing: '-0.025em', maxWidth: 1180, margin: 0, ...anim('clFade', 0.3) }}>{headline}</h1>
        )}
        {lines && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {lines.map((l, i) => (
              <h1 key={i} style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 56, lineHeight: 1.05, letterSpacing: '-0.025em', margin: 0, color: (greenLast && i === lines.length - 1) ? GREEN : TEXT, ...anim('clFade', 0.3 + i * 0.4) }}>{l}</h1>
            ))}
          </div>
        )}
        <p style={{ fontSize: 24, color: MUTED, marginTop: 30, fontWeight: 500, ...anim('clFade', lines ? 1.5 : 0.5) }}>{sub}</p>
        {sub2 && <p style={{ fontSize: 17, color: GREEN, marginTop: 8, letterSpacing: '0.04em', fontWeight: 600, ...anim('clFade', 1.65) }}>{sub2}</p>}
        {site && <p style={{ fontSize: 18, color: GREEN, marginTop: 10, letterSpacing: '0.06em', ...anim('clFade', 1.75) }}>{site}</p>}
      </div>
    </Frame>
  );
}

function PositioningCard() {
  return (
    <Frame pad={false}>
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 10%' }}>
        <img src="/cloverleaf/mark.png" alt="Cloverleaf" style={{ width: 76, height: 76, objectFit: 'contain', marginBottom: 30, ...anim('clScale', 0, 0.5) }} />
        <h1 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 58, lineHeight: 1.12, letterSpacing: '-0.025em', maxWidth: 1240, margin: 0, ...anim('clFade', 0.25) }}>
          <span style={{ color: GREEN }}>AI-native</span> insurance intelligence,<br />built from the ground up.
        </h1>
      </div>
    </Frame>
  );
}

function Overview() {
  const insights = [
    { new: true, tone: 'bad', title: 'Bodily Injury Claim Severity rising', meta: 'Commercial Auto · Southeast Region · +18.4% vs expected' },
    { tone: 'ok', title: 'Written Premium ahead of plan', meta: 'All lines · +6.2% YoY' },
    { tone: 'ok', title: 'Claim Frequency easing', meta: 'Personal Auto · −2.1% QoQ' },
  ];
  return (
    <Frame>
      <AppHeader />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 22, ...anim('clFade', 0) }}>
        <div>
          <Kicker>Autonomous Analytics</Kicker>
          <h1 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 44, letterSpacing: '-0.025em', margin: '10px 0 0' }}>Good morning. Here's what changed.</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: MUTED, fontSize: 15 }}>
          <Gauge size={18} color={GREEN} /> 5 monitored metrics · 3 lines of business
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 22 }}>
        <Metric label="Written Premium" value="$486.3M" delta="+6.2%" tone="good" delay={0.06} />
        <Metric label="Earned Loss Ratio" value="64.8%" delta="+1.9 pts" tone="bad" delay={0.13} />
        <Metric label="Claim Frequency" value="5.7" delta="−2.1%" tone="good" delay={0.2} />
        <Metric label="Claim Severity" value="$18,940" delta="+8.6%" tone="bad" delay={0.27} />
        <Metric label="Retention" value="87.4%" delta="+0.8 pts" tone="good" delay={0.34} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 20 }}>
        {/* Material change */}
        <Card delay={0.42} pulse style={{ padding: 28, border: `1px solid ${RED}`, background: `linear-gradient(135deg, rgba(255,77,79,0.10), ${TINT})`, position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: RED_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={28} color={RED} />
            </div>
            <div>
              <div style={{ fontFamily: HEAD, fontSize: 30, fontWeight: 800 }}>1 Material Change Detected</div>
              <div style={{ color: MUTED, fontSize: 15 }}>Requires review · flagged this morning</div>
            </div>
          </div>
          <div style={{ fontSize: 19, fontWeight: 700, marginBottom: 6 }}>Bodily Injury Claim Severity — Commercial Auto</div>
          <p style={{ color: MUTED, fontSize: 16, lineHeight: 1.5, margin: '0 0 18px' }}>
            Actual severity has moved <span style={{ color: RED, fontWeight: 700 }} data-cd-anchor="1">+18.4% above the expected range</span> in the Southeast Region over the most recent periods.
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 18px', borderRadius: 999, background: GREEN, color: '#04140A', fontWeight: 800, fontSize: 15 }} data-testid="review-material-change-btn">
            Review material change <ChevronRight size={17} />
          </div>
        </Card>

        {/* Insights feed */}
        <Card delay={0.5} style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <div style={{ fontFamily: HEAD, fontSize: 20, fontWeight: 800 }}>Recent insights</div>
            <span style={{ fontSize: 13, color: MUTED }}>updated 6:04 AM</span>
          </div>
          {insights.map((it, i) => (
            <div key={i} style={{ display: 'flex', gap: 14, padding: '16px 0', borderBottom: i < insights.length - 1 ? `1px solid ${BORDER}` : 'none', ...anim('clFade', 0.62 + i * 0.08) }}>
              <div style={{ width: 10, height: 10, borderRadius: 999, marginTop: 6, background: it.tone === 'bad' ? RED : GREEN }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 17, fontWeight: 700 }}>{it.title}</span>
                  {it.new && <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', color: '#04140A', background: GREEN, padding: '2px 8px', borderRadius: 6 }}>NEW</span>}
                </div>
                <div style={{ color: MUTED, fontSize: 14, marginTop: 4 }}>{it.meta}</div>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </Frame>
  );
}

function DetectedRisk() {
  return (
    <Frame>
      <AppHeader />
      <Kicker>Insight · Emerging Risk</Kicker>
      <h1 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 40, letterSpacing: '-0.025em', margin: '10px 0 22px' }}>Bodily Injury Claim Severity — Commercial Auto</h1>
      <div style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 22 }}>
        <Card delay={0.1} style={{ padding: '26px 26px 18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Average severity vs expected range</div>
            <div style={{ display: 'flex', gap: 20 }}>
              <LegendDot color={RED} label="Actual" />
              <LegendDot color={MUTED} dash label="Expected" />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 18, height: 12, background: 'rgba(147,161,184,0.25)', borderRadius: 3 }} />
                <span style={{ fontSize: 13, color: MUTED }}>Expected range</span>
              </div>
            </div>
          </div>
          <SeverityChart forecast={false} />
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 6, padding: '8px 14px', borderRadius: 999, background: RED_SOFT, color: RED, fontWeight: 700, fontSize: 14 }}>
            <AlertTriangle size={15} /> Divergence began in the most recent 5 periods
          </div>
        </Card>
        <Card delay={0.25} style={{ padding: '10px 24px 22px' }}>
          <Stat label="Current status" value="Material adverse change" tone="bad" delay={0.35} />
          <Stat label="Current severity" value="$27,860" delay={0.43} />
          <Stat label="Variance from expected" value="+18.4%" tone="bad" delay={0.51} pulse anchor />
          <Stat label="Segment" value="Southeast Region" delay={0.59} />
          <Stat label="Detection date" value="Sep 14, 2026" delay={0.67} />
          <div style={{ paddingTop: 14, ...anim('clFade', 0.75) }}>
            <div style={{ fontSize: 13, color: MUTED, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Confidence</div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999, background: GREEN_SOFT, color: GREEN, fontWeight: 800, fontSize: 16 }}><ShieldCheck size={16} /> High</span>
          </div>
        </Card>
      </div>
    </Frame>
  );
}

function Forecast() {
  return (
    <Frame>
      <AppHeader />
      <Kicker>Insight · Predictive Impact</Kicker>
      <h1 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 40, letterSpacing: '-0.025em', margin: '10px 0 22px' }}>Bodily Injury Claim Severity — Commercial Auto</h1>
      <div style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 22 }}>
        <Card delay={0.1} style={{ padding: '26px 26px 18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Actual, expected & 12-month forecast</div>
            <div style={{ display: 'flex', gap: 18 }}>
              <LegendDot color={RED} label="Actual" />
              <LegendDot color={MUTED} dash label="Expected" />
              <LegendDot color={GREEN} dash label="Forecast" />
            </div>
          </div>
          <SeverityChart forecast />
          <div style={{ fontSize: 13, color: MUTED, marginTop: 6 }}>Best-fit forecast model selected automatically · shaded area shows the confidence band.</div>
        </Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card delay={0.25} kf="clScale" style={{ padding: 26, background: `linear-gradient(135deg, rgba(54,180,75,0.12), ${TINT})`, border: `1px solid ${GREEN}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <TrendingUp size={22} color={GREEN} />
              <span style={{ fontSize: 15, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Projected 12-Month Impact</span>
            </div>
            <div style={{ fontFamily: HEAD, fontSize: 52, fontWeight: 800, letterSpacing: '-0.03em', color: TEXT, transformOrigin: 'left', ...(MOTION ? { animation: `clValuePulse 0.85s ease ${FILM_ON ? LEADER_LAND : 0.65}s 1 both` } : {}) }} data-cd-anchor="1">+$4.2M</div>
            <div style={{ fontSize: 17, color: MUTED, marginBottom: 20 }}>additional incurred loss</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
              <div>
                <div style={{ fontSize: 13, color: MUTED, marginBottom: 4 }}>Forecast confidence</div>
                <div style={{ fontFamily: HEAD, fontSize: 24, fontWeight: 800, color: GREEN }}>87%</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, color: MUTED, marginBottom: 4 }}>Expected range</div>
                <div style={{ fontFamily: HEAD, fontSize: 24, fontWeight: 800 }}>$3.5M–$4.9M</div>
              </div>
            </div>
          </Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, padding: '16px 24px', borderRadius: 16, background: GREEN, color: '#04140A', fontWeight: 800, fontSize: 18, animation: MOTION ? 'clFade .55s cubic-bezier(.22,.61,.36,1) .5s both, clGlow 2.4s ease 1.2s infinite' : undefined }} data-testid="investigate-why-btn">
            <Search size={19} /> Investigate Why
          </div>
        </div>
      </div>
    </Frame>
  );
}

function RootCause() {
  const chips = ['Commercial Auto', 'Southeast Region', 'Last 6 periods'];
  return (
    <Frame>
      <AppHeader />
      <Kicker>Investigation · Root Cause</Kicker>
      <h1 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 38, letterSpacing: '-0.025em', margin: '10px 0 14px' }}>Why is Commercial Auto bodily injury severity increasing?</h1>
      <div style={{ display: 'flex', gap: 10, marginBottom: 22, ...anim('clFade', 0.05) }}>
        {chips.map((c) => (
          <span key={c} style={{ padding: '8px 16px', borderRadius: 999, background: SURFACE, border: `1px solid ${BORDER}`, fontSize: 14, fontWeight: 600, color: MUTED }}>{c}</span>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr', gap: 22 }}>
        <Card delay={0.1} style={{ padding: 26 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <Sparkles size={20} color={GREEN} />
            <span style={{ fontFamily: HEAD, fontSize: 19, fontWeight: 800 }}>AI narrative summary</span>
          </div>
          <p style={{ fontSize: 19, lineHeight: 1.6, color: TEXT, margin: 0 }}>
            The increase is concentrated in <span style={{ color: GREEN, fontWeight: 700 }}>attorney-represented bodily injury claims</span> from urban territories. Late reporting is amplifying development in the most recent periods.
          </p>
          <div style={{ marginTop: 22, padding: '14px 16px', borderRadius: 14, background: GREEN_SOFT, display: 'flex', alignItems: 'center', gap: 10 }}>
            <BadgeCheck size={18} color={GREEN} />
            <span style={{ fontSize: 14, color: GREEN, fontWeight: 600 }}>Based on governed data — certified metrics only</span>
          </div>
        </Card>
        <Card delay={0.2} style={{ padding: 26 }}>
          <div style={{ fontFamily: HEAD, fontSize: 19, fontWeight: 800, marginBottom: 20 }}>Primary drivers of the change</div>
          <Driver rank={1} label="Attorney-represented claims" pct={42} top delay={0.35} anchor />
          <Driver rank={2} label="Urban territories" pct={31} top delay={0.5} />
          <Driver rank={3} label="Late-reported claims" pct={17} delay={0.65} />
          <Driver rank={4} label="Other factors" pct={10} delay={0.8} />
        </Card>
      </div>
    </Frame>
  );
}

const EVIDENCE = [
  'Attorney-represented BI claims up 42% share of severity change',
  'Urban territory concentration accounts for 31% of the movement',
  'Late-reported claims amplifying recent-period development (+17%)',
  'Severity now +18.4% above the certified expected range',
  'Pattern isolated to Commercial Auto, Southeast Region',
  'Forecast projects +$4.2M incurred loss at 87% confidence',
];

function Recommendation({ expanded }) {
  return (
    <Frame>
      <AppHeader />
      <Kicker>Governed Recommendation</Kicker>
      <h1 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 38, letterSpacing: '-0.025em', margin: '10px 0 22px' }}>Prioritize early intervention for high-severity bodily injury claims</h1>
      <div style={{ display: 'grid', gridTemplateColumns: expanded ? '1fr 1fr' : '1.4fr 1fr', gap: 22 }}>
        <Card delay={0.1} style={{ padding: 28 }}>
          <div style={{ fontSize: 14, color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Recommended action</div>
          <p style={{ fontSize: 20, lineHeight: 1.55, margin: '0 0 24px' }}>
            Review bodily injury authority thresholds and route attorney-represented claims in high-severity urban territories to senior adjusters within 24 hours.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <SupportItem icon={Target} label="Estimated avoidable loss" value="$1.1M–$1.6M" accent delay={0.25} anchor />
            <SupportItem icon={Clock} label="Expected time to impact" value="60–90 days" delay={0.33} />
            <SupportItem icon={ShieldCheck} label="Confidence" value="High" accent delay={0.41} />
            <SupportItem icon={GitBranch} label="Alignment" value="Claims severity reduction" delay={0.49} />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 20px', borderRadius: 12, background: expanded ? GREEN_SOFT : GREEN, color: expanded ? GREEN : '#04140A', fontWeight: 800, fontSize: 15, border: expanded ? `1px solid ${GREEN}` : 'none' }} data-testid="review-evidence-btn"><FileCheck2 size={17} /> Review Evidence</button>
            <button style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 20px', borderRadius: 12, background: SURFACE2, color: TEXT, fontWeight: 700, fontSize: 15, border: `1px solid ${BORDER}` }}><Send size={16} /> Send for Approval</button>
            <button style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 20px', borderRadius: 12, background: SURFACE2, color: TEXT, fontWeight: 700, fontSize: 15, border: `1px solid ${BORDER}` }}><ClipboardList size={16} /> Create Action Plan</button>
          </div>
        </Card>

        {expanded ? (
          <Card delay={0.3} style={{ padding: 26 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <FileCheck2 size={20} color={GREEN} />
              <span style={{ fontFamily: HEAD, fontSize: 19, fontWeight: 800 }}>Supporting evidence</span>
              <span style={{ marginLeft: 'auto', fontSize: 13, color: GREEN, fontWeight: 700 }}>6 findings</span>
            </div>
            {EVIDENCE.map((e, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '13px 0', borderBottom: i < EVIDENCE.length - 1 ? `1px solid ${BORDER}` : 'none', ...anim('clFade', 0.45 + i * 0.08) }}>
                <CheckCircle2 size={19} color={GREEN} style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 15.5, lineHeight: 1.45 }}>{e}</span>
              </div>
            ))}
          </Card>
        ) : (
          <Card delay={0.3} style={{ padding: 28, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                <BadgeCheck size={22} color={GREEN} />
                <span style={{ fontFamily: HEAD, fontSize: 19, fontWeight: 800 }}>Status</span>
              </div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 999, background: GREEN_SOFT, color: GREEN, fontWeight: 800, fontSize: 16 }}>Ready for review</span>
            </div>
            <div style={{ marginTop: 24, padding: '18px 20px', borderRadius: 14, background: SURFACE2, display: 'flex', alignItems: 'center', gap: 14 }}>
              <FileCheck2 size={26} color={GREEN} />
              <div>
                <div style={{ fontFamily: HEAD, fontSize: 26, fontWeight: 800 }}>6 supporting findings</div>
                <div style={{ fontSize: 14, color: MUTED }}>Impact, evidence & confidence attached</div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Frame>
  );
}

function SupportItem({ icon: Icon, label, value, accent, delay = 0, anchor }) {
  const pd = (anchor && FILM_ON) ? LEADER_LAND : delay + 0.5;
  return (
    <div style={{ padding: '16px 18px', borderRadius: 14, background: SURFACE2, border: `1px solid ${BORDER}`, ...anim('clFade', delay) }} {...(anchor ? { 'data-cd-anchor': '1' } : {})}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: MUTED, marginBottom: 8 }}>
        <Icon size={16} color={accent ? GREEN : MUTED} />
        <span style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      </div>
      <div style={{ fontFamily: HEAD, fontSize: 22, fontWeight: 800, color: accent ? GREEN : TEXT }}><span style={accent && MOTION ? { display: 'inline-block', transformOrigin: 'left', animation: `clValuePulse 0.85s ease ${pd}s 1 both` } : {}}>{value}</span></div>
    </div>
  );
}

function Governance() {
  const badges = [
    { icon: BadgeCheck, title: 'Certified Metric', sub: 'Claim Severity · steward approved' },
    { icon: Database, title: 'Source Data Verified', sub: 'Cloverleaf Analytics Demo DW' },
    { icon: FileCheck2, title: 'Business Definition Approved', sub: 'Reviewed by governance' },
    { icon: GitBranch, title: 'Calculation Version 3.2', sub: 'Current published version' },
    { icon: Clock, title: 'Data refreshed recently', sub: 'Today · 6:04 AM' },
    { icon: ShieldCheck, title: 'High Confidence', sub: 'Governed, deterministic evidence' },
  ];
  return (
    <Frame>
      <AppHeader />
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 30 }}>
          <Kicker>Trust & Governance</Kicker>
          <h1 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 44, letterSpacing: '-0.025em', margin: '12px 0 8px' }}>Every answer grounded in trusted data</h1>
          <p style={{ fontSize: 18, color: MUTED, margin: 0 }}>This recommendation is backed by certified metrics and verified lineage.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 18 }}>
          {badges.map((b, i) => (
            <Card key={i} kf="clPop" delay={i * 0.1} style={{ padding: 26, background: `linear-gradient(135deg, rgba(54,180,75,0.08), ${TINT})`, border: `1px solid rgba(54,180,75,0.35)` }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: GREEN_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <b.icon size={30} color={GREEN} />
              </div>
              <div style={{ fontFamily: HEAD, fontSize: 21, fontWeight: 800, marginBottom: 6 }}>{b.title}</div>
              <div style={{ fontSize: 15, color: MUTED }}>{b.sub}</div>
            </Card>
          ))}
        </div>
      </div>
    </Frame>
  );
}

/* Secure Intelligence slide (90s cut) — a dedicated scene between Act and Trust
   showing how Cloverleaf combines advanced AI reasoning with a protected data
   boundary, insurance-specific intelligence, and customer business context. */
function SecurePillar({ icon: Icon, title, text, delay = 0 }) {
  return (
    <div style={{ padding: 24, borderRadius: 18, background: SURFACE, border: `1px solid ${BORDER}`, boxShadow: PANEL_SHADOW, ...anim('clFade', delay) }}>
      <div style={{ width: 52, height: 52, borderRadius: 14, background: GREEN_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
        <Icon size={28} color={GREEN} />
      </div>
      <div style={{ fontFamily: HEAD, fontSize: 20, fontWeight: 800, marginBottom: 8, color: TEXT }}>{title}</div>
      <div style={{ fontSize: 15, color: MUTED, lineHeight: 1.45 }}>{text}</div>
    </div>
  );
}

function SecureIntel() {
  const layerLabel = (children, delay) => (
    <div style={{ fontFamily: BODY, fontWeight: 800, fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: MUTED, marginBottom: 12, ...anim('clFade', delay) }}>{children}</div>
  );
  return (
    <Frame>
      <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Kicker>Secure Intelligence</Kicker>
          <h1 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 46, letterSpacing: '-0.025em', margin: '12px 0 8px' }}>Powerful AI. <span style={{ color: GREEN }}>Protected data.</span></h1>
          <p style={{ fontSize: 19, color: MUTED, margin: 0 }}>Secure, always-on conversational Autonomous Analytics that understands your business.</p>
        </div>
        {layerLabel('Intelligence', 0.4)}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          <SecurePillar icon={Brain} title="Power of large LLMs" text="Advanced reasoning plans investigations, recognizes patterns, and explains findings." delay={0.5} />
          <SecurePillar icon={Building2} title="Built for insurance" text="Understands P&C terminology, metrics, workflows, and the questions insurers ask." delay={1.1} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '22px 0', ...anim('clFade', 0.75) }}>
          <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${GREEN})` }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 20px', borderRadius: 999, background: GREEN_SOFT, border: `1px solid rgba(54,180,75,0.45)` }}>
            <ShieldCheck size={20} color={GREEN} />
            <span style={{ fontFamily: BODY, fontWeight: 700, fontSize: 14.5, color: TEXT }}>Governed data boundary — customer data never crosses to external LLMs</span>
          </div>
          <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, ${GREEN}, transparent)` }} />
        </div>
        {layerLabel('Protected customer data', 1.5)}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          <SecurePillar icon={Lock} title="Never shared with external LLMs" text="Policy, claims, customer & financial data stay inside the governed Cloverleaf environment." delay={1.6} />
          <SecurePillar icon={BadgeCheck} title="Grounded in your business" text="Uses your certified metrics, definitions, thresholds, and operating rules." delay={2.1} />
        </div>
      </div>
    </Frame>
  );
}

/* ── Scene registry ──────────────────────────────────────────────────── */
function renderState(state) {
  switch (state) {
    case 'brand-open':
      return <BrandCard lines={['See what others miss.', "Know what's coming next."]} greenLast={false} sub="Cloverleaf Autonomous Analytics" />;
    case 'positioning':
      return <PositioningCard />;
    case 'brand-close':
      return <BrandCard lines={['See what others miss.', "Know what's coming.", 'Act before the impact.']} sub="Cloverleaf Autonomous Analytics" sub2="AI-native insurance intelligence" />;
    case 'detected-risk':
      return <DetectedRisk />;
    case 'forecast':
      return <Forecast />;
    case 'root-cause':
      return <RootCause />;
    case 'recommendation':
      return <Recommendation expanded={false} />;
    case 'recommendation-evidence':
      return <Recommendation expanded />;
    case 'governance':
      return <Governance />;
    case 'secure-intel':
      return <SecureIntel />;
    case 'overview':
    default:
      return <Overview />;
  }
}

/* Segments — state, seconds, stage, and the lower-left narrative card copy.
   Durations sum to the revised 90s editing-brief timeline (adds the dedicated
   Secure Intelligence slide between Act and Trust, with longer scene holds). */
const STAGES = ['Detect', 'Measure', 'Predict', 'Explain', 'Act', 'Trust'];
const FILM = [
  { state: 'brand-open', secs: 6 },
  { state: 'positioning', secs: 6 },
  { state: 'overview', secs: 9, stage: 'Detect', msg: 'A hidden claim severity trend is emerging.', hi: 'claim severity trend' },
  { state: 'detected-risk', secs: 9, stage: 'Measure', msg: 'Severity is 18.4% above the expected range.', hi: '18.4% above' },
  { state: 'forecast', secs: 11, stage: 'Predict', msg: 'Cloverleaf forecasts a $4.2M financial impact.', hi: '$4.2M' },
  { state: 'root-cause', secs: 10, stage: 'Explain', msg: 'AI pinpoints the drivers behind the increase.', hi: 'the drivers', sub: '73% is concentrated in two identifiable drivers.' },
  { state: 'recommendation', secs: 10, stage: 'Act', msg: 'Prioritize high-severity claims within 24 hours.', hi: 'within 24 hours', sub: 'Potential avoidable loss: $1.1M–$1.6M.' },
  { state: 'secure-intel', secs: 17 },
  { state: 'governance', secs: 6, stage: 'Trust', msg: 'Certified metrics. Verified sources. Explainable results.', hi: 'Verified sources' },
  { state: 'brand-close', secs: 6 },
];

/* Subtle edge-dim to pull the eye to the evidence without losing context. */
function Vignette() {
  return <div style={{ position: 'absolute', inset: 0, zIndex: 15, pointerEvents: 'none', background: VIGNETTE }} />;
}

/* Designed narrative card — lower-left. Enters ~1.1s after the screen (so the
   viewer reads the app + value highlight first), exits just before the next scene. */
function NarrativeCard({ stage, msg, hi, sub, exiting }) {
  const parts = hi ? msg.split(hi) : [msg];
  const activeIdx = STAGES.indexOf(stage);
  const enter = 'clSlideL 0.4s cubic-bezier(.22,.61,.36,1) 1.15s both';
  const exit = 'clSlideOut 0.35s ease 0s both';
  return (
    <div id="cd-narrative-card" style={{ position: 'absolute', left: 56, bottom: 64, width: '44%', zIndex: 20, animation: MOTION ? (exiting ? exit : enter) : undefined }}>
      {/* stage chips */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {STAGES.map((s, i) => (
          <span key={s} style={{ fontFamily: BODY, fontWeight: 800, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '5px 9px', borderRadius: 6, color: i === activeIdx ? '#04140A' : (i < activeIdx ? GREEN : MUTED), background: i === activeIdx ? GREEN : (i < activeIdx ? GREEN_SOFT : 'rgba(147,161,184,0.10)') }}>{s}</span>
        ))}
      </div>
      <div style={{ background: PANEL_BG, backdropFilter: 'blur(10px)', borderLeft: `4px solid ${GREEN}`, borderRadius: '4px 16px 16px 4px', padding: '22px 28px', boxShadow: PANEL_SHADOW }}>
        <div style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 38, lineHeight: 1.18, color: TEXT, letterSpacing: '-0.01em' }}>
          {hi ? <>{parts[0]}<span style={{ color: GREEN }}>{hi}</span>{parts[1]}</> : msg}
        </div>
        {sub && <div style={{ fontFamily: BODY, fontSize: 17, color: MUTED, fontWeight: 500, marginTop: 12 }}>{sub}</div>}
      </div>
    </div>
  );
}

/* Leader line — a thin green connector drawn from the narrative card to the
   exact on-screen evidence it describes ([data-cd-anchor]). Measured after the
   card and evidence have settled, so it lands as one visual unit. */
function LeaderLine({ active }) {
  const [geo, setGeo] = useState(null);
  useEffect(() => {
    setGeo(null);
    if (!active) return undefined;
    const t = setTimeout(() => {
      const card = document.getElementById('cd-narrative-card');
      const tgt = document.querySelector('[data-cd-anchor]');
      if (!card || !tgt) return;
      const c = card.getBoundingClientRect();
      const g = tgt.getBoundingClientRect();
      setGeo({
        sx: c.left + c.width * 0.68, sy: c.top + 4,
        tx: g.left + g.width / 2, ty: g.bottom,
      });
    }, 1550);
    return () => clearTimeout(t);
  }, [active]);
  if (!active || !geo) return null;
  const { sx, sy, tx, ty } = geo;
  const path = `M ${sx} ${sy} C ${sx} ${(sy + ty) / 2}, ${tx} ${(sy + ty) / 2}, ${tx} ${ty + 6}`;
  const len = Math.hypot(tx - sx, ty - sy) + 400;
  return (
    <svg style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', zIndex: 19, pointerEvents: 'none' }}>
      <path d={path} fill="none" stroke={LEADER_GREEN} strokeWidth={2} strokeLinecap="round"
        style={{ filter: `drop-shadow(0 0 5px ${LEADER_GREEN})`, strokeDasharray: len, strokeDashoffset: MOTION ? len : 0, animation: MOTION ? 'cdDraw 0.55s cubic-bezier(.22,.61,.36,1) 0s forwards' : undefined }} />
      <circle cx={sx} cy={sy} r={3.5} fill={LEADER_GREEN} />
      <g style={{ transformOrigin: `${tx}px ${ty + 6}px`, animation: MOTION ? 'cdRing 1.8s ease 0.55s infinite' : undefined }}>
        <circle cx={tx} cy={ty + 6} r={8} fill="none" stroke={LEADER_GREEN} strokeWidth={2} />
      </g>
    </svg>
  );
}

/* Autoplay film — sequences all scenes with the revised timeline + narrative cards (?film=1). */
function Film({ loop }) {
  const [idx, setIdx] = useState(0);
  const [exiting, setExiting] = useState(false);
  useEffect(() => {
    const seg = FILM[idx];
    const last = idx === FILM.length - 1;
    setExiting(false);
    if (last && !loop) return undefined;
    const timers = [];
    if (seg.stage) timers.push(setTimeout(() => setExiting(true), seg.secs * 1000 - 420));
    timers.push(setTimeout(() => setIdx((i) => (i + 1) % FILM.length), seg.secs * 1000));
    return () => timers.forEach(clearTimeout);
  }, [idx, loop]);
  MOTION = true;
  FILM_ON = true;
  const seg = FILM[idx];
  // Whole-scene fade-in over the continuous dark/light base = a soft crossfade
  // on every cut; because brand-open and brand-close share the same centered
  // logo + background, the loop point reads as a seamless dissolve. Brand cards
  // use a longer dissolve for an even smoother wrap.
  const isBrand = seg.state === 'brand-open' || seg.state === 'brand-close';
  return (
    <div key={idx} style={{ position: 'relative', animation: MOTION ? `cdSceneIn ${isBrand ? 0.9 : 0.5}s ease both` : undefined }}>
      {renderState(seg.state)}
      {seg.stage && <Vignette />}
      {seg.stage && <NarrativeCard stage={seg.stage} msg={seg.msg} hi={seg.hi} sub={seg.sub} exiting={exiting} />}
      {seg.stage && !exiting && <LeaderLine active />}
    </div>
  );
}

/* ── Router ──────────────────────────────────────────────────────────── */
export default function ConferenceDemoPage() {
  const [params] = useSearchParams();
  applyTheme(params.get('theme') === 'light');
  MOTION = params.get('motion') !== '0';
  if (params.get('film') === '1') return <Film loop={params.get('loop') === '1'} />;
  FILM_ON = false;
  return renderState(params.get('state') || 'overview');
}
